import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, NavController, PopoverController } from '@ionic/angular';
import Markdown from 'markdown-it';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { CONVERSATION_STATUS, DEFAULT_PRIORITY_TOKENS, routes } from 'src/app/constants';
import { getVisibleFields } from 'src/app/utils/custom-fields';
import { createPriorityValueToTokenMap, isUrl, removeUndefinedFields } from 'src/app/utils/utils';
import {
  Conversation,
  Customer,
  CustomField,
  InboundEmailRule,
  PriorityTokens,
  Profile,
  SlackChannel,
  TicketType,
} from 'src/models';
import { Account } from 'src/models/accounts';
import {
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS,
} from 'src/models/conversation-links';
import { Project } from 'src/models/projects';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { TokenService } from 'src/services/token.service';
import { UsersService } from 'src/services/users.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { CustomFieldValues, TicketTypeFieldsComponent } from '../ticket-type-fields/ticket-type-fields.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    ChannelSelectorModule,
    NgxTippyModule,
    TicketTypeFieldsComponent,
    ColonToEmojiPipe,
    SharedPipesModule,
  ],
  selector: 'app-create-conversation-modal',
  templateUrl: './create-conversation-modal.component.html',
  styleUrls: ['./create-conversation-modal.component.scss'],
})
export class CreateConversationModalComponent implements OnInit {
  @ViewChild('markdownInput', { static: false }) markdownInput;

  isLoading = false;
  markdown: string;
  title: string;
  description: string;
  type: 'slack' | 'email' | 'triage' = 'slack';
  postingType: 'channel' | 'direct' = 'channel';
  triageChannel: SlackChannel;
  channel: SlackChannel;
  customer: Partial<Customer>;
  account: Partial<Account>;
  assignee: Partial<Profile>;
  submitter: Partial<Profile>;
  project: Partial<Project>;
  ticketType: Partial<TicketType>;
  priority?: number;
  status = CONVERSATION_STATUS.IN_PROGRESS;
  allowAutoresponder = false;

  loadingInboxes = false;
  emailInboxes: Partial<InboundEmailRule>[];
  selectedEmailInbox: Partial<InboundEmailRule>;

  linkType: string;
  linkConversationId: string;
  linkConversationFriendlyId: string;
  saveCallback: (conversation: Partial<Conversation>) => {};

  projectFieldDefinitions: CustomField[] = [];
  ticketTypeFieldDefinitions: CustomField[] = [];
  renderableFieldDefinitions: CustomField[] = [];

  fieldValues: CustomFieldValues = {};
  priorityValueToTokenMap: Record<number, PriorityTokens> = createPriorityValueToTokenMap(DEFAULT_PRIORITY_TOKENS);
  public isURL = isUrl;

  private _searchablePopoverService?: SearchablePopoverService;
  private get searchablePopoverService(): SearchablePopoverService {
    if (!this._searchablePopoverService) {
      this._searchablePopoverService = this.injector.get(SearchablePopoverService);
    }
    return this._searchablePopoverService;
  }

  constructor(
    private modalCtrl: ModalController,
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private slackService: SlackService,
    private usersService: UsersService,
    public authService: AuthService,
    private msgSrvc: MessageService,
    private conversationsService: ConversationsService,
    private inboundEmailRulesService: InboundEmailrulesService,
    private projectsService: ProjectsService,
    private injector: Injector,
    private ticketTypesService: TicketTypesService,
    private cdr: ChangeDetectorRef,
    private tokenService: TokenService,
  ) {}

  ngOnInit(): void {
    // consider awaiting these
    this.loadTriageChannel();
    this.loadDefaultProject();
  }

  async initializePriorityTokens() {
    // resolves to tenant level tokens, as this is a tenant-level view
    const priorityTokens = (
      await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], this.customer?.id, this.project?.id)
    )['PRIORITY_LABEL_MAP'];
    this.priorityValueToTokenMap = createPriorityValueToTokenMap(priorityTokens);
  }

  async loadTriageChannel() {
    try {
      const triageChannel = await this.slackService.getChannel(this.authService.tenant.defaultTriageChannelId);

      if (triageChannel) {
        this.triageChannel = triageChannel;
      }
    } catch (error) {
      console.error('Error loading triage channel:', error);
      // Optionally, inform the user or set a default state
    }
  }

  async loadDefaultProject() {
    try {
      let loadedProject: Project | null = null;
      if (this.project?.id && !this.project.name) {
        loadedProject = await this.projectsService.getById(this.project.id);
      } else if (this.authService.selectedProject) {
        loadedProject = this.authService.selectedProject as Project;
      } else if (this.authService.tenant.defaultProjectId) {
        loadedProject = await this.projectsService.getById(this.authService.tenant.defaultProjectId);
      }
      this.project = loadedProject ? { ...loadedProject } : {};

      this.projectFieldDefinitions = (this.project?.fields as CustomField[]) || [];

      this.fieldValues = {};

      if (this.project?.id) {
        await this.loadDefaultTicketType();
      }

      this.initializePriorityTokens();
      this._updateRenderableFieldDefinitions();

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading default project:', error);
      // Optionally, inform the user or set a default/error state
    }
  }

  async loadDefaultTicketType() {
    try {
      this.ticketTypeFieldDefinitions = [];
      let loadedTicketType: TicketType | null = null;
      if (this.project?.defaultTicketTypeId) {
        loadedTicketType = await this.ticketTypesService.getById(this.project.defaultTicketTypeId);
      }
      this.ticketType = loadedTicketType ? { ...loadedTicketType } : {};

      this.ticketTypeFieldDefinitions = (this.ticketType?.fields as CustomField[]) || [];
    } catch (error) {
      console.error('Error loading default ticket type:', error);
      // Optionally, inform the user or set a default/error state
    }
  }

  private _updateRenderableFieldDefinitions(): void {
    let renderableDefs: CustomField[] = [...this.projectFieldDefinitions];

    const visibleTicketTypeFields = getVisibleFields(this.ticketTypeFieldDefinitions, this.fieldValues);

    renderableDefs = [...renderableDefs, ...visibleTicketTypeFields];

    const uniqueDefsMap = new Map<string, CustomField>();
    renderableDefs.forEach((def) => {
      if (def && def.id) {
        uniqueDefsMap.set(def.id, def);
      }
    });

    this.renderableFieldDefinitions = Array.from(uniqueDefsMap.values());
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.project?.id,
      callback: async (id: string, name: string, selectedProject: Project) => {
        this.project = selectedProject ? { ...selectedProject } : {};

        this.projectFieldDefinitions = (this.project?.fields as CustomField[]) || [];

        this.ticketType = {};
        this.ticketTypeFieldDefinitions = [];

        this.fieldValues = {};

        if (this.project?.id) {
          await this.loadDefaultTicketType();
        }

        this.initializePriorityTokens();
        this._updateRenderableFieldDefinitions();
        this.cdr.detectChanges();
      },
    });
  }

  selectTicketType() {
    this.searchablePopoverService.ticketTypes({
      event,
      selectedTicketTypeId: this.ticketType?.id,
      projectId: this.project?.id,
      callback: async (id: string, name: string, selectedTicketTypeObject: TicketType | undefined) => {
        this.ticketType = selectedTicketTypeObject ?? {};

        this.ticketTypeFieldDefinitions = (this.ticketType?.fields as CustomField[]) || [];

        this.fieldValues = {};
        this._updateRenderableFieldDefinitions();
        this.cdr.detectChanges();
      },
    });
  }

  onFieldValuesChange(newValues: CustomFieldValues) {
    this.fieldValues = newValues;
    this._updateRenderableFieldDefinitions();
    this.cdr.detectChanges();
  }

  selectPriority() {
    this.searchablePopoverService.priorities({
      event,
      customerId: this.customer?.id,
      projectId: this.project?.id,
      callback: async (priority: string) => {
        this.priority = priority ? Number(priority) : undefined;
      },
    });
  }

  selectStatus() {
    this.searchablePopoverService.statuses({
      event,
      selectedStatusId: this.status,
      callback: async (status: string) => {
        this.status = status;
      },
    });
  }

  selectCustomer() {
    this.searchablePopoverService.customers({
      event,
      projectId: this.project?.id,
      selectedCustomerId: this.customer?.id,
      callback: async (id: string, name: string) => {
        this.customer = {
          id,
          name,
        };
      },
    });
  }

  selectAccount() {
    this.searchablePopoverService.accounts({
      event,
      selectedAccountId: this.account?.id,
      callback: async (id: string, name: string) => {
        this.account = {
          id,
          name,
        };
      },
    });
  }

  selectAssignee() {
    this.searchablePopoverService.users({
      event,
      selectedUserId: this.assignee?.id,
      callback: async (id: string, name: string) => {
        this.assignee = {
          id,
          name,
        };
      },
    });
  }

  selectSubmitter() {
    this.searchablePopoverService.allUsers({
      event,
      selectedUserId: this.submitter?.id,
      dynamicCreationCallback: async (text: string) => {
        this.submitter = await this.usersService.create(text);
      },
      callback: async (id: string, name: string, user: any) => {
        this.submitter = {
          id,
          name,
          email: user.email,
        };
      },
    });
  }

  async showPostingOptions() {
    const CHANNEL_LABEL = 'Post to channel';
    const DM_LABEL = "Post to submitter's DM";

    const options: ActionDropdownType[] = [
      {
        label: CHANNEL_LABEL,
        icon: 'chatbubble-outline',
      },
      {
        label: DM_LABEL,
        icon: 'person-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();
          this.postingType = index === 0 ? 'channel' : 'direct';
        },
      },
      event,
    });

    await popover.present();
  }

  async showPrivacyOptions() {
    const EMAIL_LABEL = 'Send replies via email';
    const SLACK_LABEL = 'Send replies via Slack';
    const PRIVATE_LABEL = 'Replies are private in triage channel';

    const options: ActionDropdownType[] = [
      {
        label: SLACK_LABEL,
        icon: 'logo-slack',
      },
      {
        label: EMAIL_LABEL,
        icon: 'mail-outline',
      },
      {
        label: PRIVATE_LABEL,
        icon: 'lock-closed-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();
          const newType = index === 0 ? 'slack' : index === 1 ? 'email' : 'triage';

          if (newType === 'slack' || newType === 'triage') {
            this.title = '';
            this.markdown = '';
          }

          this.type = newType;

          if (this.type === 'email') {
            this.loadEmailInboxes();
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  async showInboxOptions() {
    if (!this.emailInboxes?.length) {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.SETTINGS}/email`);
      this.dismiss();
    }

    const options: ActionDropdownType[] = this.emailInboxes.map((inbox) => {
      return {
        icon: 'mail',
        label: inbox.email,
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();

          this.selectedEmailInbox = this.emailInboxes[index];
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  async loadEmailInboxes() {
    this.loadingInboxes = true;
    const result = await this.inboundEmailRulesService.list({ order: ['createdAt'], descending: false });
    this.emailInboxes = result.data ?? [];

    if (this.emailInboxes.length) {
      this.selectedEmailInbox = this.emailInboxes[0];
    }

    this.loadingInboxes = false;
  }

  assignToMe() {
    this.assignee = this.authService.profile;
  }

  async submit() {
    if ((this.type === 'email' || this.type === 'triage') && !this.markdown) {
      await this.msgSrvc.show('Whoops! Please enter the issue details.');
      return;
    }

    if (this.type === 'triage' && !this.triageChannel?.id) {
      await this.msgSrvc.show('Please select a triage channel first.');
      return;
    }

    if (this.type === 'slack') {
      if (this.postingType === 'direct' && !this.submitter) {
        await this.msgSrvc.show('Please select a submitter for direct messaging.');
        return;
      }
      if (this.postingType === 'channel' && !this.channel?.id) {
        await this.msgSrvc.show('Please select a channel to post to.');
        return;
      }
    }

    this.isLoading = true;

    try {
      const conversation: any = {
        markdown: this.markdown,
        triageChannelId: this.triageChannel?.id,
        customerId: this.customer?.id,
        assignedToUserId: this.assignee?.id,
        ticketTypeId: this.ticketType?.id,
        ticketTypeFields: this.fieldValues,
        status: this.status,
        type: this.type,
        priority: this.priority,
        accountId: this.account?.id,
        allowAutoresponder: this.allowAutoresponder,
      };

      if (this.type === 'slack') {
        conversation.projectId = this.project?.id;
        conversation.channelId = this.postingType === 'channel' ? this.channel?.id : undefined;
      }

      if (this.title) {
        conversation.title = this.title;
      }

      if (this.description) {
        conversation.description = this.description;
      }

      if (this.submitter) {
        conversation.onBehalfOf = {
          name: this.submitter.name,
          email: this.submitter.email,
          id: this.submitter.id,
        };
      }

      if (this.type === 'email') {
        if (!this.selectedEmailInbox) {
          this.msgSrvc.show('Set up an email inbox first');
          return;
        }

        if (!this.submitter) {
          this.msgSrvc.show('Select a submitter to continue');
          return;
        }

        conversation.emailInboxId = this.selectedEmailInbox.id;
      }

      const result = await this.conversationsService.create(removeUndefinedFields(conversation));

      if (this.saveCallback) {
        this.saveCallback(result);
      } else {
        this.navController.navigateRoot(`/${routes.DASHBOARD}/${routes.INBOX}/all/conversations/${result.id}`);
      }

      this.dismiss();
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  onTriageChannelChanged(slackChannel?: SlackChannel) {
    this.triageChannel = slackChannel;
  }

  getLinkageLabel() {
    return `${CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[this.linkType]} ${this.linkConversationFriendlyId ? '#' + this.linkConversationFriendlyId : ''}`;
  }

  getLinkageIcon() {
    return CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[this.linkType];
  }

  getPriorityIcon() {
    return this.priorityValueToTokenMap[this.priority]?.EMOJI;
  }

  getPriorityLabel() {
    return this.priorityValueToTokenMap[this.priority]?.LABEL;
  }

  renderMarkdown(content: string): string {
    if (!content) return '';

    const md = new Markdown({
      breaks: true,
      linkify: true,
      html: true,
    });

    return md.render(content);
  }
}
